import { useState } from "react";
import {
  ButtonContainer,
  EditPageContainer,
  EditPageHeader,
  InputContainer,
  InputLabel,
  StyledInputElement,
} from "./EditPage.styles";
import { addCountryCode, validEmail, validPhoneNumber } from "src/utils";
import { GhostButton, PrimaryButton } from "src/components/Buttons";
import type { Dealer, DealerFormData } from "./DealersPage";

const REQUIRED_FIELDS = ["id", "name", "email"] as const;
const ALPHABET = "abcdefghijklmnopqrstuvwxyz".split("");

type NormalizedFormData = Record<string, string | string[]>;

const normalizeDealerDataToFormData = (
  dealerData: Dealer,
): NormalizedFormData => {
  const normalizedData: NormalizedFormData = { ...dealerData };
  delete normalizedData.created_at;
  delete normalizedData.last_visit;
  delete normalizedData.los_id;

  if (
    typeof normalizedData.phone_number === "string" &&
    normalizedData.phone_number
  ) {
    if (normalizedData.phone_number.length === 12) {
      normalizedData.phone_number = normalizedData.phone_number.slice(2);
    } else if (normalizedData.phone_number.length === 11) {
      normalizedData.phone_number = normalizedData.phone_number.slice(1);
    }
  }

  if (Array.isArray(normalizedData.document_copies_email_list)) {
    normalizedData.document_copies_email_list =
      normalizedData.document_copies_email_list.join(", ");
  }

  return normalizedData;
};

interface EditDealerPageProps {
  dealerData: Dealer;
  closeHandler: () => void;
  saveHandler: (
    formData: DealerFormData,
    isNewDealer: boolean,
  ) => Promise<void>;
  isNewDealer: boolean;
}

export default ({
  dealerData,
  closeHandler,
  saveHandler,
  isNewDealer,
}: EditDealerPageProps) => {
  if (!dealerData) {
    return null;
  }

  const [formData, setFormData] = useState<NormalizedFormData>(
    normalizeDealerDataToFormData(dealerData),
  );

  const handleSave = async () => {
    const invalidFields = getInvalidFields();

    if (invalidFields.length > 0) {
      alert(
        `Input field invalid or empty for: \n- ${invalidFields.join("\n- ")}`,
      );
      return;
    }

    const formattedFormData = getFormattedFormData();
    await saveHandler(formattedFormData, isNewDealer);
  };

  const getInvalidFields = () => {
    const invalidFields: string[] = REQUIRED_FIELDS.filter((rField) => {
      const value = formData[rField] as string | undefined;
      if (rField === "email") {
        return !validEmail(value ?? "");
      }
      if (rField === "name") {
        return (
          (value ?? "")
            .split("")
            .filter((char: string) => ALPHABET.includes(char.toLowerCase()))
            .length < 2
        );
      }
      return !value;
    });

    const phoneNumber = formData.phone_number as string | undefined;
    if (phoneNumber?.length && !validPhoneNumber(phoneNumber)) {
      invalidFields.push("phone_number");
    }

    if (formData.document_copies_email_list?.length) {
      const emails = (formData.document_copies_email_list as string)
        .split(",")
        .map((email) => email.trim())
        .filter((email) => email !== "");

      if (emails.find((email) => !validEmail(email))) {
        invalidFields.push("document_copies_email_list");
      }
    }
    return invalidFields;
  };

  const getFormattedFormData = (): DealerFormData => {
    const formattedFormData: Record<string, string | string[]> =
      Object.fromEntries(
        Object.entries(formData).filter(([, value]) => !!value),
      );

    if (formattedFormData.name) {
      formattedFormData.name = (formattedFormData.name as string).trim();
    }

    if (formattedFormData.phone_number) {
      formattedFormData.phone_number = addCountryCode(
        formattedFormData.phone_number as string,
      );
    }

    if (formattedFormData.document_copies_email_list) {
      formattedFormData.document_copies_email_list = (
        formattedFormData.document_copies_email_list as string
      )
        .split(",")
        .map((email) => email.trim())
        .filter((email) => email !== "");
    }

    if (isNewDealer) {
      const dealer_id = formattedFormData.id;
      delete formattedFormData.id;
      formattedFormData.dealer_id = dealer_id;
    } else if ("id" in formattedFormData) {
      const new_dealer_id = formattedFormData.id;
      delete formattedFormData.id;
      formattedFormData.new_dealer_id = new_dealer_id;
    }

    return formattedFormData as DealerFormData;
  };

  return (
    <EditPageContainer>
      <EditPageHeader>
        {isNewDealer ? "Create New Dealer" : `Edit Dealer: ${dealerData.name}`}
      </EditPageHeader>
      <ButtonContainer>
        <GhostButton onClick={closeHandler}>Cancel</GhostButton>
        <PrimaryButton
          onClick={() => {
            void handleSave();
          }}
        >
          {isNewDealer ? "Create" : "Save"}
        </PrimaryButton>
      </ButtonContainer>
      {Object.keys(formData).map((key) => (
        <InputContainer key={key}>
          <InputLabel htmlFor={key}>{key}</InputLabel>
          <StyledInputElement
            id={key}
            name={key}
            type="text"
            defaultValue={String(formData[key] ?? "")}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                [key]: e.target.value,
              }))
            }
            inputSize="m"
            showError={false}
            hasValue={false}
          />
        </InputContainer>
      ))}
    </EditPageContainer>
  );
};
